// ABOUTME: React Query mutation hooks for creating, updating, and deleting unloading methods configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import {
  UnloadingMethodsApiResponse,
  UnloadingMethodsFormValues,
  UpsertUnloadingMethodsRequest,
} from '../types/unloadingMethods'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

const toApiRequest = (values: UnloadingMethodsFormValues): UpsertUnloadingMethodsRequest => ({
  values: values.values,
})

export const useCreateUnloadingMethods = (
  countryId: string | undefined,
  businessLine: string | undefined
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: UnloadingMethodsFormValues }) => {
      const res = await api.post<UnloadingMethodsApiResponse>(
        `/tuc-configs/unloading-methods/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unloadingMethodsConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'unloadingMethod', countryId, businessLine] })
    },
  })
}

export const useCreateUnloadingMethodsWithScope = (
  countryId: string | undefined,
  businessLine: string | undefined
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values }: { scope: PendingScopeRequest; values: UnloadingMethodsFormValues }) => {
      const res = await api.post<UnloadingMethodsApiResponse>(
        '/tuc-configs/unloading-methods',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unloadingMethodsConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'unloadingMethod', countryId, businessLine] })
    },
  })
}

export const useUpdateUnloadingMethods = (
  countryId: string | undefined,
  businessLine: string | undefined
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: UnloadingMethodsFormValues }) => {
      const res = await api.put<UnloadingMethodsApiResponse>(
        `/tuc-configs/unloading-methods/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unloadingMethodsConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'unloadingMethod', countryId, businessLine] })
    },
  })
}

export const useDeleteUnloadingMethods = (
  countryId: string | undefined,
  businessLine: string | undefined
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/unloading-methods/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unloadingMethodsConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'unloadingMethod', countryId, businessLine] })
    },
  })
}
